/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ClearConversationScratchpadPathParameters,
  GetConversationScratchpadPathParameters,
  TLConversationScratchpadResponse,
  TLHTTPValidationError,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Clear Conversation Scratchpad
 */
export const clearConversationScratchpad = (
  { conversationId }: ClearConversationScratchpadPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/conversations/${conversationId}/scratchpad`, method: 'DELETE', signal }, options);
};

export const getClearConversationScratchpadQueryKey = ({
  conversationId,
}: ClearConversationScratchpadPathParameters) => {
  return ['DELETE', 'conversations', conversationId, 'scratchpad'] as const;
};

export const getClearConversationScratchpadQueryOptions = <
  TData = Awaited<ReturnType<typeof clearConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: ClearConversationScratchpadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof clearConversationScratchpad>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getClearConversationScratchpadQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof clearConversationScratchpad>>> = ({ signal }) =>
    clearConversationScratchpad({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof clearConversationScratchpad>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ClearConversationScratchpadQueryResult = NonNullable<
  Awaited<ReturnType<typeof clearConversationScratchpad>>
>;
export type ClearConversationScratchpadQueryError = ErrorType<TLHTTPValidationError>;

export function useClearConversationScratchpad<
  TData = Awaited<ReturnType<typeof clearConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ClearConversationScratchpadPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof clearConversationScratchpad>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof clearConversationScratchpad>>,
          TError,
          Awaited<ReturnType<typeof clearConversationScratchpad>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useClearConversationScratchpad<
  TData = Awaited<ReturnType<typeof clearConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ClearConversationScratchpadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof clearConversationScratchpad>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof clearConversationScratchpad>>,
          TError,
          Awaited<ReturnType<typeof clearConversationScratchpad>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useClearConversationScratchpad<
  TData = Awaited<ReturnType<typeof clearConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ClearConversationScratchpadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof clearConversationScratchpad>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Clear Conversation Scratchpad
 */

export function useClearConversationScratchpad<
  TData = Awaited<ReturnType<typeof clearConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: ClearConversationScratchpadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof clearConversationScratchpad>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getClearConversationScratchpadQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Clear Conversation Scratchpad
 */
export const prefetchClearConversationScratchpadQuery = async <
  TData = Awaited<ReturnType<typeof clearConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: ClearConversationScratchpadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof clearConversationScratchpad>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getClearConversationScratchpadQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Clear Conversation Scratchpad
 */
export const invalidateClearConversationScratchpad = async (
  queryClient: QueryClient,
  { conversationId }: ClearConversationScratchpadPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getClearConversationScratchpadQueryKey({ conversationId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Conversation Scratchpad
 */
export const getConversationScratchpad = (
  { conversationId }: GetConversationScratchpadPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversationScratchpadResponse>(
    { url: `/conversations/${conversationId}/scratchpad`, method: 'GET', signal },
    options,
  );
};

export const getGetConversationScratchpadQueryKey = ({ conversationId }: GetConversationScratchpadPathParameters) => {
  return ['conversations', conversationId, 'scratchpad'] as const;
};

export const getGetConversationScratchpadQueryOptions = <
  TData = Awaited<ReturnType<typeof getConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetConversationScratchpadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationScratchpad>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConversationScratchpadQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConversationScratchpad>>> = ({ signal }) =>
    getConversationScratchpad({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getConversationScratchpad>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetConversationScratchpadQueryResult = NonNullable<Awaited<ReturnType<typeof getConversationScratchpad>>>;
export type GetConversationScratchpadQueryError = ErrorType<TLHTTPValidationError>;

export function useGetConversationScratchpad<
  TData = Awaited<ReturnType<typeof getConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationScratchpadPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationScratchpad>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConversationScratchpad>>,
          TError,
          Awaited<ReturnType<typeof getConversationScratchpad>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConversationScratchpad<
  TData = Awaited<ReturnType<typeof getConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationScratchpadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationScratchpad>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConversationScratchpad>>,
          TError,
          Awaited<ReturnType<typeof getConversationScratchpad>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConversationScratchpad<
  TData = Awaited<ReturnType<typeof getConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationScratchpadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationScratchpad>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Conversation Scratchpad
 */

export function useGetConversationScratchpad<
  TData = Awaited<ReturnType<typeof getConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetConversationScratchpadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationScratchpad>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConversationScratchpadQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Conversation Scratchpad
 */
export const prefetchGetConversationScratchpadQuery = async <
  TData = Awaited<ReturnType<typeof getConversationScratchpad>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: GetConversationScratchpadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationScratchpad>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConversationScratchpadQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Conversation Scratchpad
 */
export const invalidateGetConversationScratchpad = async (
  queryClient: QueryClient,
  { conversationId }: GetConversationScratchpadPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConversationScratchpadQueryKey({ conversationId }) }, options);

  return queryClient;
};
